import dayjs from 'dayjs';
import { getAppRole, getAuthUser } from 'utils';

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Field, Formik } from 'formik';

import { DatePicker, Input, Select, Spin } from 'antd';

import { PushpinOutlined } from '@ant-design/icons';

import { BtnPrimary } from 'components/shared/Button';
import CustomTimePicker from 'components/shared/CustomTimePicker';
import ModalMessage from 'components/shared/ModalMessage';
import UserIcon from 'components/shared/UserIcon';
import { COLORS } from 'constants/colors';
import { ROLES } from 'constants/global';
import { Community, Meeting } from 'constants/types';
import { useUpdateMeetingMutation } from 'services/meetingManagement';
import { useGetAllTopicsCategoriesQuery } from 'services/topicCategoryManagement';

import {
  SettingsWrapper,
  StyledDateFormSection,
  StyledFormSection,
  StyledInput,
  StyledLocationFormSection,
  StyledModal,
  StyledSectionLabel,
  StyledTextArea,
} from '../../Create/CreateMeetingModal/styles';
import {
  Bullet,
  CommunityName,
  PinPostWrapper,
  StyledCategoryLabel,
  StyledFormItem,
  StyledHeader,
  StyledLabel,
  StyledPinButton,
  StyledPostCategory,
  StyledRole,
  StyledSelectWrapper,
  StyledUserDetails,
  StyledUserName,
} from '../../Create/CreateTopicModal/styles';
import {
  useGetCommunitiesQuery,
  useGetCommunityQuery,
} from 'oyofrontend/communityManagement';

const { Option } = Select;

interface EditMeetingModalProps {
  meeting: Meeting;
  isEditModalOpen: boolean;
  onClose: () => void;
  refetchPinned: () => void;
  refetchUnpinned: () => void;
}

const EditMeetingModal: React.FC<EditMeetingModalProps> = ({
  meeting,
  isEditModalOpen,
  onClose,
  refetchPinned,
  refetchUnpinned,
}) => {
  const { t } = useTranslation('forum');
  const [updateMeeting] = useUpdateMeetingMutation();
  const { data: categories = [] } = useGetAllTopicsCategoriesQuery();
  const { data: communities = [] } = useGetCommunitiesQuery();
  const { data: community } = useGetCommunityQuery();
  const userRole = getAppRole();
  const [isAllCommunitiesSelected, setIsAllCommunitiesSelected] =
    useState(true);
  const [isSubmitting, setSubmitting] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [triggerClose, setTriggerClose] = useState(false);

  const handleCommunityChange = (
    value: string[] | string,
    setFieldValue: any,
  ) => {
    if (value.includes('all_communities')) {
      setFieldValue(
        'communities',
        communities.map((community: Community) => community.id),
      );
      setIsAllCommunitiesSelected(true);
    } else {
      setFieldValue('communities', value);
      setIsAllCommunitiesSelected(false);
    }
  };

  const onSaveEdit = async (values: any) => {
    setSubmitting(true);
    try {
      await updateMeeting({ id: meeting._id, updatedMeeting: values }).unwrap();
      refetchPinned();
      refetchUnpinned();
      onClose();
    } catch (error) {
      console.error('Failed to update meeting:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleModalClose = (resetForm: any, dirty: boolean) => {
    if (dirty && !triggerClose) {
      setShowWarningModal(true);
    } else {
      resetForm();
      onClose();
      setTriggerClose(false);
    }
  };

  const handleConfirmClose = (resetForm: any) => {
    setTriggerClose(true);
    handleModalClose(resetForm, false);
    setShowWarningModal(false);
  };

  return (
    <Formik
      initialValues={{
        meetingTitle: meeting.meetingTitle || '',
        meetingDescription: meeting.meetingDescription || '',
        communities: Array.isArray(meeting.communities)
          ? meeting.communities
          : [],
        category: meeting.category || '',
        date: meeting.date || '',
        time: meeting.time || '',
        location: meeting.location || '',
        pinned: meeting.pinned || false,
      }}
      onSubmit={onSaveEdit}
    >
      {({ values, setFieldValue, handleSubmit, isValid, resetForm, dirty }) => (
        <>
          <StyledModal
            title={t('edit_meeting')}
            visible={isEditModalOpen}
            onCancel={() => handleModalClose(resetForm, dirty)}
            footer={null}
          >
            <Spin
              spinning={isSubmitting}
              size="large"
              tip={t('loading')}
              style={{ color: COLORS.Green8 }}
            >
              <StyledHeader>
                <StyledUserDetails>
                  <UserIcon
                    firstName={getAuthUser().firstName}
                    lastName={getAuthUser().lastName}
                    userRole={getAppRole()}
                  />
                  <div>
                    <StyledUserName>
                      <StyledRole>
                        {userRole === ROLES.PMO ? 'PMO' : 'OYO'}
                      </StyledRole>
                      <Bullet>•</Bullet>
                      <CommunityName>
                        {userRole === ROLES.PMO
                          ? community?.communityName
                          : 'OYO Communities'}
                      </CommunityName>
                    </StyledUserName>

                    <StyledPostCategory>
                      <StyledCategoryLabel>
                        {t('post_category')}
                      </StyledCategoryLabel>
                      <StyledSelectWrapper>
                        <Select
                          getPopupContainer={(trigger) => trigger.parentNode}
                          onChange={(value) => setFieldValue('category', value)}
                          value={values.category}
                        >
                          {categories.map((category) => (
                            <Option key={category._id} value={category._id}>
                              {t(category.title)}
                            </Option>
                          ))}
                        </Select>
                      </StyledSelectWrapper>
                    </StyledPostCategory>
                  </div>
                </StyledUserDetails>
                <PinPostWrapper>
                  <StyledPinButton
                    pinned={values.pinned}
                    icon={<PushpinOutlined />}
                    onClick={() => setFieldValue('pinned', !values.pinned)}
                  >
                    {values.pinned ? t('pinned_post') : t('pin_post')}
                  </StyledPinButton>
                </PinPostWrapper>
              </StyledHeader>

              {userRole === ROLES.OYO_ADMIN && (
                <StyledFormItem>
                  <StyledLabel>{t('share_meeting_in')}</StyledLabel>
                  <Select
                    mode="multiple"
                    style={{ width: '100%', marginLeft: '6px' }}
                    getPopupContainer={(trigger) => trigger.parentNode}
                    onChange={(value) =>
                      handleCommunityChange(value, setFieldValue)
                    }
                    value={
                      Array.isArray(values.communities)
                        ? values.communities
                        : []
                    }
                  >
                    <Option key="all_communities" value="all_communities">
                      {t('all_communities')}
                    </Option>
                    {communities.map((community: Community) => (
                      <Option key={community.id} value={community.id}>
                        {community.communityName}
                      </Option>
                    ))}
                  </Select>
                </StyledFormItem>
              )}

              <StyledFormSection>
                <StyledSectionLabel>{t('meeting_title')}</StyledSectionLabel>
                <Field
                  name="meetingTitle"
                  as={StyledInput}
                  placeholder={t('meeting_title')}
                />
              </StyledFormSection>

              <StyledFormSection>
                <StyledSectionLabel>
                  {t('meeting_description')}
                </StyledSectionLabel>
                <Field
                  name="meetingDescription"
                  as={StyledTextArea}
                  placeholder={t('meeting_description')}
                />
              </StyledFormSection>

              <StyledDateFormSection>
                <StyledSectionLabel>{t('date_time')}</StyledSectionLabel>
                <SettingsWrapper>
                  <DatePicker
                    style={{ width: '280px' }}
                    placeholder={t('select_date')}
                    getPopupContainer={(trigger) =>
                      (trigger.parentNode as HTMLElement) || document.body
                    }
                    value={
                      values.date ? dayjs(values.date, 'YYYY-MM-DD') : null
                    }
                    onChange={(date) =>
                      setFieldValue(
                        'date',
                        date ? date.format('YYYY-MM-DD') : '',
                      )
                    }
                  />
                  <CustomTimePicker
                    value={values.time}
                    onChange={(time) => setFieldValue('time', time)}
                    placeholder="Select time"
                    style={{ width: '160px', marginLeft: '32px' }}
                  />
                </SettingsWrapper>
              </StyledDateFormSection>

              <StyledLocationFormSection>
                <StyledSectionLabel>{t('location')}</StyledSectionLabel>
                <Field
                  name="location"
                  as={Input}
                  placeholder={t('add_link_location')}
                />
              </StyledLocationFormSection>

              <BtnPrimary
                isDisabled={!isValid || isSubmitting || !dirty}
                onClick={() => handleSubmit()}
                additionalStyles={{ width: '100%', height: '32px' }}
              >
                {t('post')}
              </BtnPrimary>
            </Spin>
          </StyledModal>
          {showWarningModal && (
            <ModalMessage
              messageType="warining"
              title={t('unsaved_changes')}
              subTitle={t('changes_not_saved_warning')}
              btnText={t('discard')}
              handleBtn={() => handleConfirmClose(resetForm)}
              onCancelClick={() => setShowWarningModal(false)}
              btnWidth="82px"
            />
          )}
        </>
      )}
    </Formik>
  );
};

export default EditMeetingModal;
